<template>
  <v-dialog
    :value="value"
    :fullscreen="isMobile"
    :hide-overlay="isMobile"
    width="55%"
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-col class="d-flex justify-space-between pb-0">
        <v-card-text class="text-body-1">
          {{ $t("print") }} {{ $t("accumulative") }}
        </v-card-text>
        <v-btn icon color="grey darken-2" @click="close">
          <v-icon class="close-hover" large> mdi-close </v-icon>
        </v-btn>
      </v-col>

      <v-row class="mx-0 justify-right my-2 mr-5">
        <v-container
          class="wrapper-toggle d-flex align-center pa-0 mx-0"
          style="width: 220px"
        >
          <div
            v-for="(item, index) in aggregateOptions"
            :key="index"
            class="child flex-fill text-center"
            :style="{
              'background-color': selectedMethod === item.value ? '#0066ff' : '',
              'border-color': selectedMethod === item.value ? '#0066ff' : 'silver',
              color: selectedMethod === item.value ? '#fff' : '',
            }"
            @click="selectMethod(item)"
          >
            <v-card-text
              class="pa-0 text-caption"
              :style="{ color: selectedMethod === item.value ? '#fff' : '' }"
            >
              {{ item.label }}
            </v-card-text>
          </div>
        </v-container>
      </v-row>

      <v-row class="ma-0 justify-right mr-5">
        <template v-if="selectedMethod === 'daily'">
          <v-col cols="12" md="4" class="pr-0">
            <v-text-field
              id="aggregateFromDate"
              v-model="fromDate"
              :label="$t('from') + ' ' + $t('date')"
              outlined
              dense
              clearable
            />
            <date-picker
              v-model="fromDate"
              element="aggregateFromDate"
              :locale="$store.state.date_locale"
              :format="$store.state.date_format"
              :max="toDate"
              auto-submit
              :color="
                $vuetify.theme.themes[$vuetify.theme.isDark ? 'dark' : 'light'].primary
              "
              @close="setDate"
            />
          </v-col>
          <v-col cols="12" md="4" class="pr-0">
            <v-text-field
              id="aggregateToDate"
              v-model="toDate"
              :label="$t('until') + ' ' + $t('date')"
              outlined
              dense
              clearable
            />
            <date-picker
              v-model="toDate"
              element="aggregateToDate"
              :locale="$store.state.date_locale"
              :format="$store.state.date_format"
              :min="fromDate"
              auto-submit
              :color="
                $vuetify.theme.themes[$vuetify.theme.isDark ? 'dark' : 'light'].primary
              "
              @close="setDate"
            />
          </v-col>
        </template>
        <template v-else>
          <v-col cols="12" md="4" class="pr-0">
            <v-text-field
              id="aggregateFromMonth"
              v-model="fromMonth"
              :label="$t('from') + ' ' + $t('monthly')"
              outlined
              dense
            />
            <date-picker
              v-model="fromMonth"
              element="aggregateFromMonth"
              locale="fa,en"
              auto-submit
              type="year-month"
              format="jYYYY/jMM"
              :min="$store.state.financialYear.start_date"
              :max="$store.state.financialYear.end_date"
              simple
              :color="
                $vuetify.theme.themes[$vuetify.theme.isDark ? 'dark' : 'light'].primary
              "
            />
          </v-col>
          <v-col cols="12" md="4" class="pr-0">
            <v-text-field
              id="aggregateToMonth"
              v-model="toMonth"
              :label="$t('until') + ' ' + $t('monthly')"
              outlined
              dense
            />
            <date-picker
              v-model="toMonth"
              element="aggregateToMonth"
              locale="fa,en"
              auto-submit
              type="year-month"
              format="jYYYY/jMM"
              :min="fromMonth || $store.state.financialYear.start_date"
              :max="$store.state.financialYear.end_date"
              simple
              :color="
                $vuetify.theme.themes[$vuetify.theme.isDark ? 'dark' : 'light'].primary
              "
            />
          </v-col>
        </template>
        <v-col cols="12" md="4">
          <v-select
            v-model="priority"
            :label="$t('priorityAccount')"
            :items="priorityItems"
            item-text="text"
            item-value="value"
            dense
            outlined
            height="32"
          />
        </v-col>
      </v-row>

      <v-card-actions class="mt-2 pb-4 pr-5 pe-3">
        <v-btn color="primary" height="32" :loading="loading" @click="submitPrint">
          {{ $t("print") }}
        </v-btn>
        <v-spacer />
        <v-btn color="grey" class="pe-0" height="32" @click="close">
          {{ $t("close") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import General from "../../../../../Mixins/general";

export default {
  name: "AggregateDialog",
  mixins: [General],
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    selectedMethod: "daily",
    fromDate: null,
    toDate: null,
    fromMonth: null,
    toMonth: null,
    priority: "total",
  }),
  computed: {
    aggregateOptions() {
      return [
        { label: this.$t("dailyAggregate"), value: "daily" },
        { label: this.$t("monthlyAggregate"), value: "monthly" },
      ];
    },
    priorityItems() {
      return [
        { value: "total", text: this.$t("total") },
        { value: "given", text: this.$t("given") },
        { value: "detail", text: this.$t("detail") },
      ];
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.selectedMethod = "daily";
        this.initDefaults("daily");
      }
    },
  },
  mounted() {
    this.fromDate = this.$store.state.financialYear.start_date;
    this.toDate = this.$store.state.endDate;
  },

  methods: {
    initDefaults(method = this.selectedMethod) {
      if (method === "daily") {
        this.fromDate = this.$store.state.financialYear.start_date;
        this.toDate = this.$store.state.endDate;
        this.fromMonth = null;
        this.toMonth = null;
      } else {
        this.fromDate = null;
        this.toDate = null;
        const [y, m] = this.$store.state.endDate.split("/");
        this.fromMonth = `${y}/${m}`;
        this.toMonth = `${y}/${m}`;
      }
    },
    selectMethod(item) {
      this.selectedMethod = item.value;
      this.initDefaults(item.value);
    },
    close() {
      this.$emit("input", false);
    },
    submitPrint() {
      const payload =
        this.selectedMethod === "daily"
          ? {
              method: "daily",
              fromDate: this.fromDate,
              toDate: this.toDate,
              fromMonth: null,
              toMonth: null,
              Priority: this.priority,
            }
          : {
              method: "monthly",
              fromDate: null,
              toDate: null,
              fromMonth: this.fromMonth,
              toMonth: this.toMonth,
              Priority: this.priority,
            };

      this.$emit("print", payload);
    },
  },
};
</script>

<style scoped>
.wrapper-toggle {
  border: 1px solid silver;
  border-radius: 4px;
}
.wrapper-toggle .child {
  padding: 7.7px;
  cursor: pointer;
  border: 1px solid silver;
  border-radius: 4px;
}
.wrapper-toggle .child:first-child {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
.wrapper-toggle .child:nth-child(2) {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}
</style>
